import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Ban, RotateCcw, History, ShieldCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-CH", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

export default function UserStatusControl({ u, selfId, onChanged }) {
  const { t } = useTranslation();
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState(null);
  const canToggle = !u.protected && u.id !== selfId;

  const submit = async () => {
    if (reason.trim().length < 3) { toast.error(t("admin_status.reason_short")); return; }
    setBusy(true);
    try {
      await api.post(`/admin/users/${u.id}/${u.disabled ? "enable" : "disable"}`, { reason: reason.trim() });
      toast.success(t(u.disabled ? "admin_status.done_enable" : "admin_status.done_disable"));
      setAsking(false); setReason(""); setLog(null); onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const toggleLog = async () => {
    if (log) { setLog(null); return; }
    const { data } = await api.get(`/admin/users/${u.id}/status-log`); setLog(data.items);
  };

  return (
    <div className="mt-2 space-y-2" data-testid={`user-status-${u.id}`}>
      {u.disabled && <p className="text-[11px] text-red-700" data-testid={`user-disabled-info-${u.id}`}>{t("admin_status.since", { date: fmt(u.disabled_at), reason: u.disabled_reason || "" })}</p>}
      <div className="flex flex-wrap gap-2">
        {u.protected && <span className="text-[11px] text-text-secondary flex items-center gap-1" data-testid={`user-protected-${u.id}`}><ShieldCheck className="w-3.5 h-3.5" />{t("admin_status.protected")}</span>}
        {canToggle && !asking && (
          <button type="button" onClick={() => setAsking(true)} data-testid={`user-${u.disabled ? "enable" : "disable"}-btn-${u.id}`}
            className={`h-9 px-3 rounded-xl text-xs font-semibold flex items-center gap-1.5 border ${u.disabled ? "border-primary-c text-primary-c" : "border-red-300 text-red-700"}`}>
            {u.disabled ? <RotateCcw className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}{t(u.disabled ? "admin_status.enable" : "admin_status.disable")}
          </button>
        )}
        <button type="button" onClick={toggleLog} className="h-9 px-3 rounded-xl text-xs font-semibold flex items-center gap-1.5 border border-c" data-testid={`user-status-log-btn-${u.id}`}>
          <History className="w-3.5 h-3.5" />{t("admin_status.log")}
        </button>
      </div>
      {asking && (
        <div className="p-3 rounded-xl bg-surface-muted space-y-2" data-testid={`user-status-confirm-${u.id}`}>
          <p className="text-xs font-semibold">{t(u.disabled ? "admin_status.confirm_enable" : "admin_status.confirm_disable")}</p>
          <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} placeholder={t("admin_status.reason")} className="input-c text-sm" data-testid={`user-status-reason-${u.id}`} />
          <div className="flex gap-2">
            <button type="button" disabled={busy} onClick={submit} className="btn-primary !py-2 text-sm flex-1" data-testid={`user-status-submit-${u.id}`}>{t("admin_status.confirm")}</button>
            <button type="button" onClick={() => { setAsking(false); setReason(""); }} className="btn-secondary !py-2 text-sm">{t("admin_status.cancel")}</button>
          </div>
        </div>
      )}
      {log && (
        <ul className="text-[11px] space-y-1" data-testid={`user-status-log-${u.id}`}>
          {log.length === 0 && <li className="text-text-secondary">{t("admin_status.log_empty")}</li>}
          {log.map((l, i) => (
            <li key={l.at + i} className="p-2 rounded-lg bg-surface-muted">
              <strong>{t(`admin_status.act_${l.action}`)}</strong> · {fmt(l.at)} · {t("admin_status.by")} {l.by} — {l.reason}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
